import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { store } from "./state";
import { type Item, type Opening, type Project } from "./types";
import { roomBaseY, roomCorners, roomHeightRange, WALL_FACE_LABEL, type WallFace } from "./room";
import { wallApertures } from "./wall";
import {
  brickTexture,
  fieldTexture,
  grassTexture,
  stuccoTexture,
  tileTexture,
  waterTexture,
  woodTexture,
} from "./textures";

const WALL_THICK = 0.22;
const ROOM_COLORS = [0xe8d5b5, 0xd4e4c8, 0xc9d6e8, 0xf0cfc9, 0xe6d9f0, 0xf5e6c8];

export class EditorScene {
  readonly renderer: THREE.WebGLRenderer;
  readonly camera: THREE.PerspectiveCamera;
  readonly controls: OrbitControls;
  readonly root = new THREE.Group();
  readonly pickables: THREE.Object3D[] = [];
  ground?: THREE.Mesh;

  private scene = new THREE.Scene();
  private sun: THREE.DirectionalLight;
  private itemGroups = new Map<string, THREE.Group>();
  private dimGroup = new THREE.Group();
  private mats = {
    stucco: new THREE.MeshStandardMaterial({ map: stuccoTexture(), roughness: 0.92 }),
    brick: new THREE.MeshStandardMaterial({ map: brickTexture(), roughness: 0.88 }),
    tiles: new THREE.MeshStandardMaterial({ map: tileTexture(), roughness: 0.7 }),
    grassRoof: new THREE.MeshStandardMaterial({ map: grassTexture(3, 3), roughness: 1 }),
    chapa: new THREE.MeshStandardMaterial({ color: 0x8a9298, roughness: 0.35, metalness: 0.55 }),
    roofFlat: new THREE.MeshStandardMaterial({ color: 0xb8b3aa, roughness: 0.9 }),
    water: new THREE.MeshStandardMaterial({
      map: waterTexture(),
      roughness: 0.12,
      metalness: 0.05,
      transparent: true,
      opacity: 0.88,
    }),
    wood: new THREE.MeshStandardMaterial({ map: woodTexture(), roughness: 0.7 }),
    concrete: new THREE.MeshStandardMaterial({ color: 0xc9c4bb, roughness: 0.95 }),
    glass: new THREE.MeshStandardMaterial({
      color: 0x87c6d6,
      roughness: 0.05,
      metalness: 0.15,
      transparent: true,
      opacity: 0.55,
    }),
    frame: new THREE.MeshStandardMaterial({ color: 0x2a2a28, roughness: 0.6 }),
    coping: new THREE.MeshStandardMaterial({ color: 0xeeeae2, roughness: 0.8 }),
    tree: new THREE.MeshStandardMaterial({ color: 0x2f6b2a, roughness: 1 }),
    trunk: new THREE.MeshStandardMaterial({ color: 0x5a3a22, roughness: 1 }),
    ceramic: new THREE.MeshStandardMaterial({ color: 0xf4f1ea, roughness: 0.25 }),
    cabinet: new THREE.MeshStandardMaterial({ color: 0xc4a574, roughness: 0.7 }),
    counter: new THREE.MeshStandardMaterial({ color: 0x5c5c5a, roughness: 0.4 }),
    column: new THREE.MeshStandardMaterial({ color: 0xb4afa6, roughness: 0.88 }),
    losa: new THREE.MeshStandardMaterial({ color: 0x9a9590, roughness: 0.92 }),
    select: new THREE.MeshBasicMaterial({ color: 0xc46a3a, wireframe: true }),
    handle: new THREE.MeshStandardMaterial({ color: 0xc46a3a, roughness: 0.45, emissive: 0x5a2a12, emissiveIntensity: 0.25 }),
    handleDoor: new THREE.MeshStandardMaterial({ color: 0x3d7a4a, roughness: 0.45, emissive: 0x1a3d22, emissiveIntensity: 0.2 }),
  };
  private preview = new THREE.Group();
  private focusMark: THREE.Group;
  private northMark = new THREE.Group();

  constructor(canvas: HTMLCanvasElement) {
    this.scene.background = new THREE.Color(0xd8e4d2);
    this.scene.fog = new THREE.Fog(0xd8e4d2, 80, 220);

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;

    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 400);
    this.camera.position.set(38, 28, 42);

    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.maxPolarAngle = Math.PI / 2 - 0.06;
    this.controls.minDistance = 2;
    this.controls.maxDistance = 160;
    this.controls.target.set(0, 0.5, 0);
    this.controls.screenSpacePanning = true;

    this.scene.add(new THREE.HemisphereLight(0xe8f0ff, 0x6b7a4e, 0.85));

    this.sun = new THREE.DirectionalLight(0xfff1d6, 1.6);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = 160;
    this.sun.shadow.camera.left = -50;
    this.sun.shadow.camera.right = 50;
    this.sun.shadow.camera.top = 50;
    this.sun.shadow.camera.bottom = -50;
    this.scene.add(this.sun);
    this.scene.add(this.sun.target);
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.18));
    this.scene.add(this.root);
    this.scene.add(this.preview);
    this.focusMark = this.makeFocusMark();
    this.scene.add(this.focusMark);
    this.syncFocusMark();
    this.rebuild();
    this.resize();
    window.addEventListener("resize", () => this.resize());
  }

  resize(): void {
    const parent = this.renderer.domElement.parentElement;
    const w = parent?.clientWidth ?? window.innerWidth;
    const h = parent?.clientHeight ?? window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h, false);
  }

  tick(): void {
    this.controls.update();
    this.syncFocusMark();
    this.renderer.render(this.scene, this.camera);
  }

  setFocus(x: number, y: number, z: number): void {
    this.controls.target.set(x, Math.max(0.15, y), z);
    this.controls.update();
    this.syncFocusMark();
  }

  focusItem(item: { x: number; z: number; height: number }): void {
    this.setFocus(item.x, Math.max(0.4, item.height * 0.45), item.z);
  }

  resetFocus(): void {
    this.setFocus(0, 0.5, 0);
  }

  private makeFocusMark(): THREE.Group {
    const g = new THREE.Group();
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.45, 0.04, 8, 28),
      new THREE.MeshBasicMaterial({ color: 0xc46a3a }),
    );
    ring.rotation.x = Math.PI / 2;
    const pin = new THREE.Mesh(new THREE.SphereGeometry(0.1, 10, 8), new THREE.MeshBasicMaterial({ color: 0xc46a3a }));
    pin.position.y = 0.08;
    g.add(ring, pin);
    return g;
  }

  private syncFocusMark(): void {
    this.focusMark.position.copy(this.controls.target);
  }

  rebuild(): void {
    const p = store.project;
    disposeGroup(this.root);
    this.pickables.length = 0;
    this.itemGroups.clear();
    this.dimGroup = new THREE.Group();

    this.buildLot(p);
    for (const item of p.items) this.buildItem(item);
    this.applyInteriorGhost(p.showInteriors);
    this.applyOverlays();
    this.updateSun();
    this.updateSelection();
  }

  applyOverlays(): void {
    const names = store.project.showNames;
    const measures = store.project.showMeasures;
    this.root.traverse((o) => {
      if (o.userData.overlay === "name") o.visible = names;
      if (o.userData.overlay === "measure") o.visible = measures;
    });
  }

  syncTransforms(): void {
    for (const item of store.project.items) {
      const g = this.itemGroups.get(item.id);
      if (!g) continue;
      g.position.set(item.x, item.kind === "field" ? 0.03 : 0, item.z);
      g.rotation.y = item.rotation;
    }
    this.updateSelection();
  }

  updateSun(hour = store.project.hour): void {
    const t = ((hour - 6) / 12) * Math.PI;
    const r = 55;
    const x = Math.cos(t) * r;
    const y = Math.sin(t) * 40 + 8;
    const z = Math.sin(t * 0.6) * 20;
    const north = store.project.north ?? 0;
    const c = Math.cos(north);
    const s = Math.sin(north);
    this.sun.position.set(x * c + z * s, y, -x * s + z * c);
    this.sun.intensity = 0.35 + Math.max(0, Math.sin(t)) * 1.4;
    this.sun.target.position.set(0, 0, 0);
    const warm = new THREE.Color().lerpColors(new THREE.Color(0xffb070), new THREE.Color(0xfff4dc), Math.sin(t));
    this.sun.color.copy(warm);
    this.syncNorthMark();
  }

  updateNorth(): void {
    this.updateSun();
  }

  updateSelection(): void {
    const ids = new Set(store.selectedIds);
    this.root.traverse((o) => {
      if (o.userData.outline) o.visible = ids.has(o.userData.itemId);
      if (o.userData.handle) o.visible = o.userData.itemId === store.selectedId && ids.size <= 1;
      if (o instanceof THREE.Mesh && o.userData.kind === "room" && o.userData.wall !== undefined) {
        const mat = o.material as THREE.MeshStandardMaterial;
        const on = o.userData.itemId === store.selectedId && store.wallFace === o.userData.wall;
        mat.emissive.setHex(on ? 0xc46a3a : 0x000000);
        mat.emissiveIntensity = on ? 0.28 : 0;
        mat.opacity = store.project.showInteriors ? (on ? 0.45 : 0.22) : on ? 0.7 : 0.42;
      }
      const openId = o.userData.openingId ?? o.userData.apertureId;
      if (o instanceof THREE.Mesh && typeof openId === "string" && o.material && "emissive" in o.material) {
        const mat = o.material as THREE.MeshStandardMaterial;
        const on = openId === store.apertureId;
        mat.emissive.setHex(on ? 0xc46a3a : 0x000000);
        mat.emissiveIntensity = on ? 0.35 : 0;
      }
    });
  }

  setWallPreview(from: { x: number; z: number } | null, to: { x: number; z: number } | null): void {
    while (this.preview.children.length) {
      const c = this.preview.children[0];
      this.preview.remove(c);
      if (c instanceof THREE.Line) c.geometry.dispose();
    }
    if (!from) return;
    const a = new THREE.Vector3(from.x, 0.08, from.z);
    const b = new THREE.Vector3(to?.x ?? from.x, 0.08, to?.z ?? from.z);
    const geo = new THREE.BufferGeometry().setFromPoints([a, b]);
    const line = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: 0xc46a3a }));
    this.preview.add(line);
    const mark = new THREE.Mesh(new THREE.SphereGeometry(0.12, 10, 8), this.mats.handle);
    mark.position.copy(a);
    this.preview.add(mark);
  }

  private buildLot(p: Project): void {
    const grass = new THREE.MeshStandardMaterial({
      map: grassTexture(p.lot.width / 4, p.lot.depth / 4),
      roughness: 1,
    });
    grass.userData.disposable = true;
    const ground = new THREE.Mesh(new THREE.PlaneGeometry(p.lot.width, p.lot.depth), grass);
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    ground.name = "lot";
    ground.userData.kind = "lot";
    this.ground = ground;
    this.root.add(ground);

    const edge = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.PlaneGeometry(p.lot.width, p.lot.depth)),
      new THREE.LineBasicMaterial({ color: 0xffffff }),
    );
    edge.rotation.x = -Math.PI / 2;
    edge.position.y = 0.02;
    this.root.add(edge);

    const lotSpan = Math.max(p.lot.width, p.lot.depth);
    const meters = Math.max(2, Math.round(lotSpan));
    const grid = new THREE.GridHelper(meters, meters, 0xffffff, 0xffffff);
    const mat = grid.material as THREE.Material;
    mat.transparent = true;
    mat.opacity = 0.12;
    mat.depthWrite = false;
    grid.position.y = 0.01;
    this.root.add(grid);

    this.addDimLine(
      this.dimGroup,
      -p.lot.width / 2,
      0,
      -p.lot.depth / 2 - 1.6,
      p.lot.width / 2,
      0,
      -p.lot.depth / 2 - 1.6,
      `${p.lot.width.toFixed(1)} m`,
    );
    this.addDimLine(
      this.dimGroup,
      -p.lot.width / 2 - 1.6,
      0,
      -p.lot.depth / 2,
      -p.lot.width / 2 - 1.6,
      0,
      p.lot.depth / 2,
      `${p.lot.depth.toFixed(1)} m`,
    );
    this.root.add(this.dimGroup);
    this.northMark = this.makeNorthMark();
    this.root.add(this.northMark);
    this.syncNorthMark();
  }

  private makeNorthMark(): THREE.Group {
    const g = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({
      color: 0xc46a3a,
      roughness: 0.55,
      emissive: 0x5a2a12,
      emissiveIntensity: 0.18,
    });
    mat.userData.disposable = true;
    const shaft = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.07, 1.35), mat);
    shaft.position.set(0, 0.05, 0.05);
    const head = new THREE.Mesh(new THREE.ConeGeometry(0.34, 0.72, 3), mat);
    head.rotation.x = Math.PI / 2;
    head.position.set(0, 0.06, 0.88);
    const letter = makeNorthLetter();
    letter.position.set(0, 0.95, 0);
    g.add(shaft, head, letter);
    return g;
  }

  private syncNorthMark(): void {
    const p = store.project;
    if (!this.northMark.parent) return;
    const north = p.north ?? 0;
    const nx = Math.sin(north);
    const nz = Math.cos(north);
    const margin = 2.4;
    const tx = Math.abs(nx) > 1e-4 ? (p.lot.width / 2 - margin) / Math.abs(nx) : Infinity;
    const tz = Math.abs(nz) > 1e-4 ? (p.lot.depth / 2 - margin) / Math.abs(nz) : Infinity;
    const dist = Math.max(1.2, Math.min(tx, tz));
    this.northMark.position.set(nx * dist, 0.04, nz * dist);
    this.northMark.rotation.y = north;
  }

  private buildItem(item: Item): void {
    const g = new THREE.Group();
    g.name = item.id;
    g.position.set(item.x, item.kind === "field" ? 0.03 : 0, item.z);
    g.rotation.y = item.rotation;
    this.itemGroups.set(item.id, g);

    if (item.kind === "house") this.buildHouse(g, item);
    else if (item.kind === "room") this.buildRoom(g, item);
    else if (item.kind === "pool") this.buildPool(g, item);
    else if (item.kind === "field") this.buildField(g, item);
    else if (item.kind === "tree") this.buildTree(g, item);
    else if (item.kind === "toilet") this.buildToilet(g, item);
    else if (item.kind === "kitchen") this.buildKitchen(g, item);
    else if (item.kind === "column") this.buildColumn(g, item);
    else if (item.kind === "canopy") this.buildCanopy(g, item);
    else if (item.kind === "wall") this.buildWall(g, item);

    this.root.add(g);
  }

  private tag(obj: THREE.Object3D, item: Item, extra?: Record<string, unknown>): void {
    obj.userData.itemId = item.id;
    obj.userData.kind = item.kind;
    if (extra) Object.assign(obj.userData, extra);
  }

  private pick(mesh: THREE.Object3D, item: Item, extra?: Record<string, unknown>): void {
    this.tag(mesh, item, extra);
    this.pickables.push(mesh);
  }

  private buildHouse(g: THREE.Group, item: Item): void {
    const w = item.width;
    const d = item.depth;
    const h = item.height;
    const found = new THREE.Mesh(new THREE.BoxGeometry(w + 0.35, 0.18, d + 0.35), this.mats.concrete);
    found.position.y = 0.09;
    found.castShadow = true;
    found.receiveShadow = true;
    g.add(found);

    const wallMat = item.wall === "brick" ? this.mats.brick : this.mats.stucco;
    const specs: { w: number; d: number; x: number; z: number; wall: 0 | 1 | 2 | 3 }[] = [
      { w, d: WALL_THICK, x: 0, z: d / 2 - WALL_THICK / 2, wall: 0 },
      { w, d: WALL_THICK, x: 0, z: -d / 2 + WALL_THICK / 2, wall: 2 },
      { w: WALL_THICK, d, x: w / 2 - WALL_THICK / 2, z: 0, wall: 1 },
      { w: WALL_THICK, d, x: -w / 2 + WALL_THICK / 2, z: 0, wall: 3 },
    ];
    for (const s of specs) {
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(s.w, h, s.d), wallMat);
      mesh.position.set(s.x, 0.18 + h / 2, s.z);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.pick(mesh, item, { wall: s.wall });
      g.add(mesh);
    }

    const floor = new THREE.Mesh(new THREE.BoxGeometry(w - WALL_THICK, 0.08, d - WALL_THICK), this.mats.concrete);
    floor.position.y = 0.22;
    this.pick(floor, item);
    g.add(floor);

    this.addRoof(g, item, w, d, h);

    for (const op of item.openings ?? []) g.add(this.makeOpening(item, op, w, d));

    this.addOutline(g, item, w + 0.6, h + 2.2, d + 0.6, 0.18 + (h + 2.2) / 2);
    this.addItemDims(g, item);
  }

  private addRoof(g: THREE.Group, item: Item, w: number, d: number, h: number): void {
    const roofKind = item.roof ?? "tiles";
    const y0 = 0.18 + h;
    if (roofKind === "tiles") {
      const roofH = Math.max(1.4, Math.min(w, d) * 0.22);
      const roof = new THREE.Mesh(new THREE.ConeGeometry(Math.hypot(w, d) * 0.55, roofH, 4), this.mats.tiles);
      roof.rotation.y = Math.PI / 4;
      roof.position.y = y0 + roofH / 2 - 0.05;
      roof.castShadow = true;
      roof.userData.roof = true;
      this.pick(roof, item);
      g.add(roof);
      return;
    }
    const slab = new THREE.Mesh(
      new THREE.BoxGeometry(w + 0.4, 0.16, d + 0.4),
      roofKind === "chapa" ? this.mats.chapa : this.mats.roofFlat,
    );
    slab.position.y = y0 + 0.08;
    slab.castShadow = true;
    slab.userData.roof = true;
    this.pick(slab, item);
    g.add(slab);
    if (roofKind === "grass") {
      const green = new THREE.Mesh(new THREE.BoxGeometry(w + 0.2, 0.12, d + 0.2), this.mats.grassRoof);
      green.position.y = y0 + 0.2;
      green.userData.roof = true;
      this.pick(green, item);
      g.add(green);
    }
    if (roofKind === "chapa") {
      for (let i = -3; i <= 3; i++) {
        const rib = new THREE.Mesh(new THREE.BoxGeometry(w + 0.35, 0.04, 0.08), this.mats.chapa);
        rib.position.set(0, y0 + 0.18, (i / 3) * (d / 2));
        rib.userData.roof = true;
        g.add(rib);
      }
    }
  }

  private applyInteriorGhost(show: boolean): void {
    const roofs = [this.mats.tiles, this.mats.grassRoof, this.mats.chapa, this.mats.roofFlat];
    for (const m of roofs) {
      m.transparent = show;
      m.opacity = show ? 0.18 : 1;
      m.depthWrite = !show;
      m.needsUpdate = true;
    }
  }

  private makeOpening(item: Item, op: Opening, w: number, d: number, yBase = 0.18): THREE.Group {
    const group = new THREE.Group();
    const isDoor = op.kind === "door";
    const ow = isDoor ? 1.0 : 1.15;
    const oh = isDoor ? 2.1 : 1.15;
    const along = op.wall === 0 || op.wall === 2 ? w : d;
    const xAlong = (op.t - 0.5) * (along - ow - 0.4);
    const y = isDoor ? yBase + oh / 2 : yBase + op.height + oh / 2;

    if (op.wall === 0) group.position.set(xAlong, y, d / 2 - WALL_THICK / 2);
    if (op.wall === 2) group.position.set(xAlong, y, -d / 2 + WALL_THICK / 2);
    if (op.wall === 1) group.position.set(w / 2 - WALL_THICK / 2, y, xAlong);
    if (op.wall === 3) group.position.set(-w / 2 + WALL_THICK / 2, y, xAlong);
    if (op.wall === 1 || op.wall === 3) group.rotation.y = Math.PI / 2;

    const frameMat = this.mats.frame.clone();
    const fillMat = (isDoor ? this.mats.wood : this.mats.glass).clone();
    frameMat.userData.disposable = true;
    fillMat.userData.disposable = true;
    const hole = new THREE.Mesh(new THREE.BoxGeometry(ow, oh, WALL_THICK + 0.12), frameMat);
    const inner = new THREE.Mesh(new THREE.BoxGeometry(ow - 0.12, oh - 0.12, 0.06), fillMat);
    inner.position.z = 0.02;
    this.pick(hole, item, { openingId: op.id });
    this.pick(inner, item, { openingId: op.id });
    group.add(hole, inner);
    return group;
  }

  private buildRoom(g: THREE.Group, item: Item): void {
    const w = item.width;
    const d = item.depth;
    const y0 = roomBaseY(item);
    const th = 0.08;
    const color = ROOM_COLORS[Math.abs(hash(item.name + item.id)) % ROOM_COLORS.length];
    const floorMat = new THREE.MeshStandardMaterial({ color, roughness: 0.85 });
    floorMat.userData.disposable = true;
    const floor = new THREE.Mesh(new THREE.BoxGeometry(w, 0.06, d), floorMat);
    floor.position.y = y0 + 0.03;
    floor.receiveShadow = true;
    this.pick(floor, item);
    g.add(floor);

    const c = roomCorners(item);
    const specs: { face: WallFace; len: number; hA: number; hB: number; x: number; z: number; rot: number }[] = [
      { face: 0, len: w, hA: c[1], hB: c[0], x: 0, z: d / 2, rot: 0 },
      { face: 2, len: w, hA: c[3], hB: c[2], x: 0, z: -d / 2, rot: 0 },
      { face: 1, len: d, hA: c[0], hB: c[2], x: w / 2, z: 0, rot: Math.PI / 2 },
      { face: 3, len: d, hA: c[3], hB: c[1], x: -w / 2, z: 0, rot: -Math.PI / 2 },
    ];
    for (const s of specs) {
      const mat = new THREE.MeshStandardMaterial({
        color,
        roughness: 0.9,
        transparent: true,
        opacity: store.project.showInteriors ? 0.22 : 0.42,
      });
      mat.userData.disposable = true;
      const mesh = new THREE.Mesh(trapezoidWall(s.len, s.hA, s.hB, th), mat);
      mesh.position.set(s.x, y0, s.z);
      mesh.rotation.y = s.rot;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.pick(mesh, item, { wall: s.face });
      g.add(mesh);
      const midH = (s.hA + s.hB) / 2;
      const hLab = makeLabel(`${WALL_FACE_LABEL[s.face]} ${midH.toFixed(2)} m`, "measure");
      hLab.position.set(s.x, y0 + midH + 0.28, s.z);
      g.add(hLab);
    }
    for (const op of item.openings ?? []) g.add(this.makeOpening(item, op, w, d, y0));

    const { max } = roomHeightRange(item);
    const label = makeLabel(item.name, "name");
    label.position.set(0, y0 + max + 0.55, 0);
    g.add(label);
    this.addOutline(g, item, w + 0.2, max + 0.2, d + 0.2, y0 + max / 2);
    this.addItemDims(g, item);
  }

  private buildPool(g: THREE.Group, item: Item): void {
    const w = item.width;
    const d = item.depth;
    const deck = new THREE.Mesh(new THREE.BoxGeometry(w + 1.2, 0.12, d + 1.2), this.mats.coping);
    deck.position.y = 0.04;
    deck.receiveShadow = true;
    this.pick(deck, item);
    g.add(deck);
    const basin = new THREE.Mesh(new THREE.BoxGeometry(w, 0.55, d), this.mats.concrete);
    basin.position.y = -0.2;
    g.add(basin);
    const water = new THREE.Mesh(new THREE.BoxGeometry(w - 0.16, 0.08, d - 0.16), this.mats.water);
    water.position.y = 0.02;
    this.pick(water, item);
    g.add(water);
    this.addOutline(g, item, w + 1.4, 0.8, d + 1.4, 0.2);
    this.addItemDims(g, item);
  }

  private buildField(g: THREE.Group, item: Item): void {
    const w = item.width;
    const d = item.depth;
    const mat = new THREE.MeshStandardMaterial({ map: fieldTexture(w, d), roughness: 1 });
    mat.userData.disposable = true;
    const pitch = new THREE.Mesh(new THREE.PlaneGeometry(w, d), mat);
    pitch.rotation.x = -Math.PI / 2;
    pitch.receiveShadow = true;
    this.pick(pitch, item);
    g.add(pitch);

    const postMat = new THREE.MeshStandardMaterial({ color: 0xf2f2f0 });
    postMat.userData.disposable = true;
    for (const side of [-1, 1]) {
      const goal = new THREE.Group();
      const barW = Math.min(3.6, d * 0.18);
      const left = new THREE.Mesh(new THREE.BoxGeometry(0.1, 1.5, 0.1), postMat);
      const right = left.clone();
      const top = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.1, barW), postMat);
      left.position.set(0, 0.75, -barW / 2);
      right.position.set(0, 0.75, barW / 2);
      top.position.set(0, 1.5, 0);
      goal.add(left, right, top);
      goal.position.set((w / 2 - 0.2) * side, 0, 0);
      g.add(goal);
    }
    this.addOutline(g, item, w, 0.2, d, 0.12);
    this.addItemDims(g, item);
  }

  private buildTree(g: THREE.Group, item: Item): void {
    const scale = Math.max(item.height / 3.2, 0.6);
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.24, 1.4, 6), this.mats.trunk);
    trunk.position.y = 0.7 * scale;
    trunk.scale.setScalar(scale);
    const crown = new THREE.Mesh(new THREE.SphereGeometry(1.15, 10, 8), this.mats.tree);
    crown.position.y = 2.1 * scale;
    crown.scale.setScalar(scale);
    crown.castShadow = true;
    this.pick(trunk, item);
    this.pick(crown, item);
    const treeName = makeLabel(item.name, "name");
    treeName.position.set(0, 3.3 * scale, 0);
    g.add(trunk, crown, treeName);
    this.addOutline(g, item, 2.6 * scale, 3.4 * scale, 2.6 * scale, 1.6 * scale);
  }

  private buildToilet(g: THREE.Group, item: Item): void {
    const bowl = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.2, 0.28, 16), this.mats.ceramic);
    bowl.position.y = 0.28;
    const seat = new THREE.Mesh(new THREE.TorusGeometry(0.2, 0.035, 8, 16), this.mats.ceramic);
    seat.rotation.x = Math.PI / 2;
    seat.position.y = 0.43;
    const tank = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.42, 0.16), this.mats.ceramic);
    tank.position.set(0, 0.55, -0.22);
    this.pick(bowl, item);
    this.pick(tank, item);
    const toiletName = makeLabel(item.name, "name");
    toiletName.position.set(0, 1.15, 0);
    g.add(bowl, seat, tank, toiletName);
    this.addOutline(g, item, item.width + 0.2, 1, item.depth + 0.2, 0.5);
  }

  private buildKitchen(g: THREE.Group, item: Item): void {
    const w = item.width;
    const d = item.depth;
    const base = new THREE.Mesh(new THREE.BoxGeometry(w, 0.72, d), this.mats.cabinet);
    base.position.y = 0.36;
    const top = new THREE.Mesh(new THREE.BoxGeometry(w + 0.04, 0.06, d + 0.04), this.mats.counter);
    top.position.y = 0.75;
    const sink = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.08, 0.32), this.mats.ceramic);
    sink.position.set(-w / 4, 0.8, 0);
    this.pick(base, item);
    this.pick(top, item);
    const kitchenName = makeLabel(item.name, "name");
    kitchenName.position.set(0, 1.2, 0);
    g.add(base, top, sink, kitchenName);
    this.addOutline(g, item, w + 0.15, 1, d + 0.15, 0.5);
  }

  private buildWall(g: THREE.Group, item: Item): void {
    const len = Math.max(0.4, item.width);
    const th = Math.max(0.08, item.depth);
    const h = Math.max(0.8, item.height);
    const wallMat = this.mats.stucco;

    const addPanel = (w: number, hh: number, y: number, lx: number) => {
      if (w < 0.04 || hh < 0.04) return;
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, hh, th), wallMat);
      mesh.position.set(lx, y, 0);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.pick(mesh, item);
      g.add(mesh);
    };

    const aps = [...wallApertures(item)].sort((a, b) => a.offset - b.offset);
    if (!aps.length) {
      addPanel(len, h, h / 2, 0);
    } else {
      let cursor = 0;
      for (const ap of aps) {
        const off = Math.min(Math.max(ap.offset, cursor), len - 0.3);
        const dw = Math.min(ap.width, len - off - 0.05);
        const dh = Math.min(ap.height, h - 0.12);
        const sill = Math.max(0, ap.sill);
        if (off - cursor > 0.04) addPanel(off - cursor, h, h / 2, -len / 2 + cursor + (off - cursor) / 2);
        const above = h - sill - dh;
        if (above > 0.04) addPanel(dw, above, sill + dh + above / 2, -len / 2 + off + dw / 2);
        if (sill > 0.03) addPanel(dw, sill, sill / 2, -len / 2 + off + dw / 2);
        const fillMat = (ap.kind === "door" ? this.mats.wood : this.mats.glass).clone();
        fillMat.userData.disposable = true;
        const fill = new THREE.Mesh(
          new THREE.BoxGeometry(dw - 0.06, Math.max(0.25, dh - 0.08), 0.05),
          fillMat,
        );
        fill.position.set(-len / 2 + off + dw / 2, sill + dh / 2, 0);
        this.pick(fill, item, { apertureId: ap.id });
        g.add(fill);
        cursor = off + dw;
      }
      if (len - cursor > 0.04) addPanel(len - cursor, h, h / 2, -len / 2 + cursor + (len - cursor) / 2);
    }

    this.addHandle(g, item, "endA", -len / 2, h / 2, 0, this.mats.handle);
    this.addHandle(g, item, "endB", len / 2, h / 2, 0, this.mats.handle);
    for (const ap of aps) {
      this.addHandle(
        g,
        item,
        "aperture",
        -len / 2 + ap.offset + ap.width / 2,
        ap.sill + ap.height / 2,
        th / 2 + 0.12,
        this.mats.handleDoor,
        ap.id,
      );
    }
    this.addOutline(g, item, len + 0.2, h + 0.15, th + 0.25, h / 2);
  }

  private addHandle(
    g: THREE.Group,
    item: Item,
    kind: "endA" | "endB" | "aperture",
    x: number,
    y: number,
    z: number,
    mat: THREE.Material,
    apertureId?: string,
  ): void {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.22, 0.22), mat);
    mesh.position.set(x, y, z);
    mesh.userData.handle = kind;
    mesh.userData.apertureId = apertureId;
    mesh.userData.itemId = item.id;
    mesh.visible = false;
    this.pickables.push(mesh);
    g.add(mesh);
  }

  private buildColumn(g: THREE.Group, item: Item): void {
    const w = Math.max(0.15, item.width);
    const d = Math.max(0.15, item.depth);
    const h = Math.max(0.4, item.height);
    const shaft = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), this.mats.column);
    shaft.position.y = h / 2;
    shaft.castShadow = true;
    shaft.receiveShadow = true;
    const cap = new THREE.Mesh(new THREE.BoxGeometry(w + 0.08, 0.08, d + 0.08), this.mats.concrete);
    cap.position.y = h + 0.02;
    const base = new THREE.Mesh(new THREE.BoxGeometry(w + 0.1, 0.1, d + 0.1), this.mats.concrete);
    base.position.y = 0.05;
    this.pick(shaft, item);
    this.pick(cap, item);
    g.add(base, shaft, cap);
    const name = makeLabel(item.name, "name");
    name.position.set(0, h + 0.75, 0);
    const hLabel = makeLabel(`h ${h.toFixed(2)} m`, "measure");
    hLabel.position.set(0, h + 0.45, 0);
    g.add(name, hLabel);
    this.addOutline(g, item, w + 0.35, h + 0.3, d + 0.35, h / 2);
  }

  private buildCanopy(g: THREE.Group, item: Item): void {
    const w = item.width;
    const d = Math.max(0.4, item.depth);
    const h0 = item.height;
    const h1 = item.heightEnd ?? item.height;
    const isLosa = item.cover === "losa";
    const thick = isLosa ? 0.16 : 0.05;
    const mat = isLosa ? this.mats.losa : this.mats.chapa;

    const deck = new THREE.Group();
    const slab = new THREE.Mesh(new THREE.BoxGeometry(w, thick, d), mat);
    slab.position.y = thick / 2;
    slab.castShadow = true;
    slab.receiveShadow = true;
    this.pick(slab, item);
    deck.add(slab);

    if (!isLosa) {
      const ribs = Math.max(5, Math.round(w / 0.7));
      for (let i = 0; i < ribs; i++) {
        const t = i / (ribs - 1);
        const x = (t - 0.5) * (w - 0.12);
        const rib = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.07, d + 0.04), this.mats.chapa);
        rib.position.set(x, thick + 0.04, 0);
        rib.castShadow = true;
        this.pick(rib, item);
        deck.add(rib);
      }
    }

    const beam = new THREE.Mesh(new THREE.BoxGeometry(w + 0.08, 0.1, 0.1), isLosa ? this.mats.losa : this.mats.chapa);
    const beamA = beam.clone();
    const beamB = beam.clone();
    beamA.position.set(0, thick / 2, -d / 2);
    beamB.position.set(0, thick / 2, d / 2);
    this.pick(beamA, item);
    this.pick(beamB, item);
    deck.add(beamA, beamB);

    deck.rotation.x = Math.atan2(h0 - h1, d);
    deck.position.y = (h0 + h1) / 2;
    g.add(deck);

    const start = makeLabel(`inicio ${h0.toFixed(2)} m`, "measure");
    start.position.set(0, h0 + 0.55, -d / 2);
    const end = makeLabel(`fin ${h1.toFixed(2)} m`, "measure");
    end.position.set(0, h1 + 0.55, d / 2);
    g.add(start, end);
    this.addOutline(g, item, w + 0.3, thick + Math.abs(h1 - h0) + 0.5, d + 0.3, (h0 + h1) / 2);
    this.addItemDims(g, item);
  }

  private addOutline(parent: THREE.Group, item: Item, w: number, h: number, d: number, y: number): void {
    const box = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), this.mats.select);
    box.position.y = y;
    box.userData.outline = true;
    box.userData.itemId = item.id;
    box.visible = false;
    parent.add(box);
  }

  private addItemDims(parent: THREE.Group, item: Item): void {
    const w = item.width;
    const d = item.depth;
    const y = item.kind === "house" ? item.height + 0.4 : 0.55;
    this.addDimLine(parent, -w / 2, y, -d / 2 - 0.55, w / 2, y, -d / 2 - 0.55, `${w.toFixed(1)} m`);
    this.addDimLine(parent, -w / 2 - 0.55, y, -d / 2, -w / 2 - 0.55, y, d / 2, `${d.toFixed(1)} m`);
    const name = makeLabel(item.name, "name");
    name.position.set(0, y + 0.7, 0);
    parent.add(name);
  }

  private addDimLine(
    target: THREE.Group,
    x1: number,
    y1: number,
    z1: number,
    x2: number,
    y2: number,
    z2: number,
    label: string,
  ): void {
    const pts = [new THREE.Vector3(x1, y1 + 0.05, z1), new THREE.Vector3(x2, y2 + 0.05, z2)];
    const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), new THREE.LineBasicMaterial({ color: 0xffffff }));
    line.userData.overlay = "measure";
    target.add(line);
    const sprite = makeLabel(label, "measure");
    sprite.position.set((x1 + x2) / 2, y1 + 0.45, (z1 + z2) / 2);
    target.add(sprite);
  }
}

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}

function makeNorthLetter(): THREE.Sprite {
  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) throw new Error("north");
  const c = ctx.canvas;
  c.width = 128;
  c.height = 128;
  ctx.fillStyle = "#c46a3a";
  ctx.font = "800 88px Inter, Segoe UI, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("N", 64, 68);
  const map = new THREE.CanvasTexture(c);
  map.colorSpace = THREE.SRGBColorSpace;
  const spriteMat = new THREE.SpriteMaterial({ map, transparent: true });
  spriteMat.userData.disposable = true;
  const spr = new THREE.Sprite(spriteMat);
  spr.scale.set(1.15, 1.15, 1);
  spr.userData.overlay = "north";
  return spr;
}

function makeLabel(text: string, overlay: "name" | "measure" = "name"): THREE.Sprite {
  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) throw new Error("label");
  const c = ctx.canvas;
  c.width = 320;
  c.height = 64;
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.roundRect(8, 8, 304, 48, 12);
  ctx.fill();
  ctx.fillStyle = "#2b2b28";
  ctx.font = "600 24px Inter, Segoe UI, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, 160, 32);
  const map = new THREE.CanvasTexture(c);
  map.colorSpace = THREE.SRGBColorSpace;
  const spriteMat = new THREE.SpriteMaterial({ map, transparent: true });
  spriteMat.userData.disposable = true;
  const spr = new THREE.Sprite(spriteMat);
  spr.scale.set(4.6, 0.95, 1);
  spr.userData.overlay = overlay;
  return spr;
}

function trapezoidWall(length: number, hA: number, hB: number, thickness: number): THREE.BufferGeometry {
  const L = length / 2;
  const a = Math.max(0.2, hA);
  const b = Math.max(0.2, hB);
  const shape = new THREE.Shape();
  shape.moveTo(-L, 0);
  shape.lineTo(L, 0);
  shape.lineTo(L, b);
  shape.lineTo(-L, a);
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: false, steps: 1 });
  geo.translate(0, 0, -thickness / 2);
  geo.computeVertexNormals();
  return geo;
}

function disposeGroup(group: THREE.Group): void {
  const children = [...group.children];
  for (const child of children) {
    child.traverse((obj) => {
      if (
        obj instanceof THREE.Mesh ||
        obj instanceof THREE.Line ||
        obj instanceof THREE.LineSegments ||
        obj instanceof THREE.Sprite
      ) {
        obj.geometry.dispose();
        const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
        for (const material of materials) {
          if (!material.userData.disposable) continue;
          const std = material as THREE.MeshStandardMaterial;
          std.map?.dispose();
          material.dispose();
        }
      }
    });
    group.remove(child);
  }
}
